import type { PropsWithChildren } from "react"
import { useRegionConfig } from "modules/i18n/hooks/useRegionConfig"
import { Link } from "common/components/Footer/Link"
import { SocialMediaLink } from "common/components/Footer/SocialMediaLink"
import { ConsentPreferencesLink } from "common/components/Footer/ConsentPreferencesLink"
import { FooterLink } from "modules/i18n/utils/loadRegionConfiguration"
import { useTranslations } from "modules/i18n/hooks/useTranslations"

interface FooterProps {
  year: number
}

export const Footer = ({ year }: PropsWithChildren<FooterProps>) => {
  const t = useTranslations("layout.footer")

  const {
    footer: {
      first_column: firstColumn,
      second_column: secondColumn,
      social_media: socialMedia,
      copyright_first_column: copyrightFirstColumn,
      copyright_notice_html: copyrightNoticeHtml,
    },
  } = useRegionConfig()

  const mapFooterLinks = (links: FooterLink[]) =>
    links.map(({ url, title, label, didomi_preferences }) => {
      if (didomi_preferences) {
        return (
          <ConsentPreferencesLink key="didomi-preferences" title={title}>
            {label}
          </ConsentPreferencesLink>
        )
      }

      return (
        <Link key={url} href={url} title={title}>
          {label}
        </Link>
      )
    })

  const firstColumnLinks = mapFooterLinks(firstColumn)
  const secondColumnLinks = mapFooterLinks(secondColumn)
  const copyrightColumnLinks = mapFooterLinks(copyrightFirstColumn)

  const socialMediaLinks = socialMedia.map(({ url, title, icon }) => (
    <SocialMediaLink key={url} title={title} href={url} icon={icon} />
  ))

  return (
    <footer
      className="mt-auto bg-gray-900 text-center text-white md:text-left lg:text-left"
      data-testid="footer"
    >
      <nav aria-label={t("footer_nav_label")}>
        <div className="tc-container container mx-auto grid grid-cols-12 py-6">
          <div className="order-2 col-span-full flex flex-col sm:col-span-4 md:order-1 xl:col-span-3">
            {firstColumnLinks}
          </div>
          <div className="order-1 col-span-full flex flex-col sm:col-span-4 md:order-2 xl:col-span-3">
            {secondColumnLinks}
          </div>
          <div
            aria-label={t("social_footer")}
            className="order-last col-span-full flex flex-row flex-wrap place-content-start justify-center gap-3 justify-self-end py-5 sm:col-span-4 sm:justify-start lg:py-0 xl:col-span-6"
          >
            {socialMediaLinks}
          </div>
        </div>

        <div className="bg-black">
          <section className="tc-container text-2xs container mx-auto flex flex-col gap-4 py-4 md:flex-row 2xl:text-xs">
            {copyrightColumnLinks}

            <div className="mx-auto lg:mr-0 lg:ml-auto">
              <span
                dangerouslySetInnerHTML={{
                  __html: copyrightNoticeHtml.replace("%{year}", year.toString()),
                }}
              />
            </div>
          </section>
        </div>
      </nav>
    </footer>
  )
}
